import { Bounce, toast } from "react-toastify";
import { Dispatch, SetStateAction } from "react";
import { PickerConfiguration } from "react-google-drive-picker/dist/typeDefs";
import {
  checkGoogleAccessTokenValidity,
  getNewAccessAndRefreshTokens,
} from "../services/auth.service";
import { Auth } from "../auth/auth";
import { UserRolesKey } from "../modules/Admin/constants";
import moment from "moment-timezone";

export const getFirstTwoInitialsOfName = (name: string) => {
  const nameSplit = name?.split(" ");
  const wordsLength = nameSplit?.length;
  let str = "";
  if (wordsLength > 1) {
    str += nameSplit[0][0];
    //  + nameSplit[1][0];
  } else if (wordsLength === 1) {
    str += nameSplit[0][0];
    // + nameSplit[0][1];
  }
  return str.toUpperCase();
};

export const parseJWTToken = (token: string) => {
  if (!token) return null;
  const [, body] = token.split(".");
  if (!body || body === undefined) return null;

  try {
    return JSON.parse(atob(body));
  } catch (e) {
    return null;
  }
};

export const filterKeys = (object: any, keys: string[]) => {
  let result: any = {};
  keys.forEach((key) => {
    if (object.hasOwnProperty(key)) {
      result[key] = object[key];
    }
  });
  return result;
};

export const changeFileType = (fileName: string, type: string) => {
  const words = fileName?.split(".");
  words.pop();
  const reName = words.join() + `.${type}`;
  return reName;
};

export const downloadFileFromUrl = (
  url: string = "",
  filename: string = ""
) => {
  const a = document.createElement("a");
  a.setAttribute("download", filename);
  a.setAttribute("href", url);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const downloadFileFromBlob = (
  url: string = "",
  filename: string = ""
) => {
  if (!url || !filename) return;
  const req = new XMLHttpRequest();
  req.open("GET", url, true);
  req.setRequestHeader("Access-Control-Allow-Origin", "*");
  req.responseType = "blob";
  req.onload = function () {
    const blob = new Blob([req.response], {
      type: "application/pdf",
    });

    if ((window.navigator as any).msSaveBlob) {
      (window.navigator as any).msSaveBlob(blob, filename);
    } else {
      const windowUrl = window.URL || window.webkitURL;
      const href = windowUrl.createObjectURL(blob);
      const a = document.createElement("a");
      a.setAttribute("download", filename);
      a.setAttribute("href", href);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };
  req.send();
};

export const getToast = (type: string, message: any) => {
  toast[type === "Success" ? "success" : "error"](message, {
    transition: Bounce,
    theme: "colored",
    hideProgressBar: true,
    closeButton: false,
    icon: false,
  });
};

export const getTotalCommentsNumber = (comments: any) => {
  let totalComments: number = 0;
  Object.keys(comments).forEach((key) => {
    totalComments += comments?.[key]?.length;
  });
  return totalComments;
};

export const getRefreshedTokensAndUpdateThePreviousTokens = async () => {
  try {
    const auth = Auth.getInstance();
    const { access_token, refresh_token } = await getNewAccessAndRefreshTokens({
      refresh_token: auth.getGoogleRefreshToken() || "",
      redirect_uri: process.env.REACT_APP_GOOGLE_AUTH_REDIRECT_URI || "",
    });
    auth.saveGoogleAccessToken(access_token);
    auth.saveGoogleRefreshToken(refresh_token);
  } catch (error) {
    getToast("Error", "Something went wrong");
  }
};

export const checkTokenValidityAndUpdateIfExpired = async () => {
  try {
    const auth = Auth.getInstance();
    const tokenInfo = await checkGoogleAccessTokenValidity(
      auth.getGoogleAccessToken() || ""
    );
    if (tokenInfo?.expires_in < 30) {
      await getRefreshedTokensAndUpdateThePreviousTokens();
    }
  } catch (error) {
    await getRefreshedTokensAndUpdateThePreviousTokens();
  }
};

declare const google: any;

export const handleDriveUploadCommonMethod = async (params: {
  setLoader: Dispatch<SetStateAction<boolean>>;
  openPicker: (config: PickerConfiguration) => boolean | undefined;
  multiselect?: boolean;
  onResponse?: Function;
  mimeTypes?: any;
  viewId?: "DOCS";
}) => {
  params?.setLoader(true);
  await checkTokenValidityAndUpdateIfExpired();
  const auth = Auth.getInstance();
  // it's ok open the picker
  params?.setLoader(false);
  params?.openPicker({
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || "",
    developerKey: "",
    token: auth.getGoogleAccessToken() || "",
    viewId: params.viewId,
    showUploadView: false,
    showUploadFolders: true,
    setIncludeFolders: true,
    supportDrives: true,
    multiselect: params?.multiselect,
    viewMimeTypes: params?.mimeTypes,
    customViews: [
      new google.picker.DocsView(google.picker.ViewId.DOCS)
        .setIncludeFolders(true)
        .setOwnedByMe(true)
        .setSelectFolderEnabled(true)
        .setMimeTypes(params.mimeTypes)
        .setLabel("My Drive"),

      new google.picker.DocsView(google.picker.ViewId.DOCS)
        .setIncludeFolders(true)
        .setOwnedByMe(false)
        .setSelectFolderEnabled(true)
        .setMimeTypes(params.mimeTypes)
        .setLabel("Shared with Me"),
    ],
    disableDefaultView: true,
    callbackFunction: async (res: any) =>
      params?.onResponse && params?.onResponse(res),
  });
};

export function copyToClip(params: {
  setLoader?: Dispatch<SetStateAction<boolean>>;
  innerHTML: string;
  onSuccess?: () => void;
  onError?: (err: any) => void;
}): void {
  try {
    params?.setLoader && params?.setLoader(true);
    const blob = new Blob([params?.innerHTML], { type: "text/html" });
    const clipboardItem = new window.ClipboardItem({ "text/html": blob });
    navigator.clipboard.write([clipboardItem]);
    params?.onSuccess && params?.onSuccess();
  } catch (error) {
    params?.onError && params?.onError(error);
  } finally {
    setTimeout(() => {
      params?.setLoader && params?.setLoader(false);
    }, 2000);
  }
}

export const getRoles = (roles: any) => {
  if (Array.isArray(roles) && roles.length > 0) {
    if (roles.length === 2) {
      const first = roles[0];
      const second = roles[1];
      if (
        first?.name === UserRolesKey.REDLINING_USER &&
        second?.name === UserRolesKey.CLAUSE_CHECK_USER
      ) {
        return "Vendor Solutions Consultant - Risk & Contracts";
      }
    }
    return roles[0]?.display_name;
  }
  return "N/A";
};

export const parseNextLine = (text: string) => {
  return text.replace(/(\\n)/g, "\n");
};

export function isOverflowActive(event: any) {
  return (
    event.offsetHeight < event.scrollHeight ||
    event.offsetWidth < event.scrollWidth
  );
}

export const getLocalDateTimeFromTimestamp = (data: {
  timestamp: any;
  dateformat?: string;
  timeformat?: string;
}) => {
  const {
    timestamp = "",
    dateformat = "MM-DD-YYYY",
    timeformat = "HH:mm",
  } = data;
  const localTimezone = moment.tz.guess();
  const localDate = moment.tz(timestamp, localTimezone);
  return {
    date: localDate.format(dateformat),
    time: localDate.format(timeformat),
  };
};

export const sliderSettings = () => {
  // Set up settings for the slider to show 4 cards at once
  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 4, // Show 4 cards at once
    slidesToScroll: 2, // Scroll 4 cards at a time
    autoplay: false,
    arrows: true,
    responsive: [
      {
        breakpoint: 1200, // Medium screens
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        },
      },
      {
        breakpoint: 768, // Small screens
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };
  return settings;
};
